import React from 'react';
import PropTypes from 'prop-types';

import backend from 'utils/backend';

import {Box, Chip, Stack, Typography} from 'views/components';
import {Check} from 'views/icons';
import {ConnectionBadge, HoverToolBox, MarkdownViewer, Translate, translate} from 'views/module';
import {TASK_TYPES} from 'views/module/task/constants';

const TaskView = (props) => {
  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const task = props.task;
  const handleSetStatusToComplete = props.handleSetStatusToComplete;
  const onClickEditTask = props.onClickEditTask;
  const removeTask = props.removeTask;

  const title = task.title;
  const description = task.description.blob;
  const dueDate = task.dueDate;
  const [loadedConnections, setLoadedConnections] = React.useState([]);
  const rawConnections = React.useMemo(() => {
    return Array.isArray(task.connections) ? task.connections : [];
  }, [task.connections]);
  const taskType = task.type;
  const mappedTaskTypeConfig = taskType ? TASK_TYPES[taskType] : null;
  const taskTypeConfig = taskType ? (mappedTaskTypeConfig ?? TASK_TYPES.UNKNOWN) : null;
  const taskTypeLabel = mappedTaskTypeConfig?.labelKey ? translate(mappedTaskTypeConfig.labelKey) : taskType;
  const taskComplete = task.status === 'COMPLETE';
  const connections = React.useMemo(() => {
    if (Array.isArray(task.connectionInfo) && task.connectionInfo.length > 0) {
      return task.connectionInfo;
    }

    if (loadedConnections.length > 0) {
      return loadedConnections;
    }

    if (rawConnections.length > 0) {
      return rawConnections.map((connectionRn) => ({
        connectionRn,
      }));
    }

    return [];
  }, [loadedConnections, rawConnections, task.connectionInfo]);

  React.useEffect(() => {
    if (Array.isArray(task.connectionInfo) && task.connectionInfo.length > 0) {
      setLoadedConnections(task.connectionInfo);
      return;
    }

    if (rawConnections.length === 0) {
      setLoadedConnections([]);
      return;
    }

    let isMounted = true;

    const fetchConnections = async () => {
      const connectionInfo = await Promise.all(rawConnections.map(async (connectionRn) => {
        try {
          if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
            const response = await backend.client.getClient(connectionRn, false);
            const clientName = response?.clientDetail?.clientName || '';
            const [firstName = '', ...lastNameParts] = clientName.trim().split(/\s+/);

            return {
              connectionRn,
              firstName,
              lastName: lastNameParts.join(' '),
            };
          }

          if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
            const response = await backend.caregiver.getCaregiver(connectionRn, false);
            const caregiverName = response?.caregiverDetail?.caregiverName || '';
            const [firstName = '', ...lastNameParts] = caregiverName.trim().split(/\s+/);

            return {
              connectionRn,
              firstName,
              lastName: lastNameParts.join(' '),
            };
          }

          return {connectionRn};
        } catch {
          return {connectionRn};
        }
      }));

      if (isMounted) {
        setLoadedConnections(connectionInfo.filter(Boolean));
      }
    };

    fetchConnections();

    return () => {
      isMounted = false;
    };
  }, [rawConnections, task.connectionInfo]);


  React.useEffect(() => {
    setToolbarVisible(mouseOver);
  }, [mouseOver]);

  return (
    <Box>
      <Box
        onMouseOver={(e) => setMouseOver(true) & setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setMouseOver(false)}
        sx={{
          backgroundColor: toolbarVisible ? 'background.highlighted' : 'inherit',
          p: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: '2px',
          }}
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, taskComplete)}
            sx={{
              'width': '14px',
              'height': '14px',
              'borderRadius': '50%',
              'border': '1.5px solid #D8D3CC',
              'backgroundColor': taskComplete ? '#DA7955' : '#F2F0EC',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': taskComplete ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: taskComplete ? '#DA7955' : '#F2F0EC',
              },
            }}
          >
            {
              taskComplete &&
              <Check
                sx={{
                  fontSize: '12px',
                  color: 'white',
                }}
              />
            }
          </Box>
        </Box>
        <Stack spacing={1} sx={{opacity: taskComplete ? 0.45 : 1}}>
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            justifyContent='space-between'
            spacing={1}
            sx={{
              pr: {xs: 0, sm: 5},
            }}
          >
            <Stack direction='row' alignItems='center' spacing={2}>
              {
                taskType &&
                <Chip
                  label={taskTypeLabel}
                  variant='outlined'
                  size='small'
                  sx={{
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderWidth: 1,
                    ...taskTypeConfig?.chipSx,
                  }}
                />
              }
              <Stack>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    textDecoration: taskComplete ? 'line-through' : 'none',
                    fontSize: '13px',
                  }}
                >
                  {title}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Stack>
            {
              description &&
              <Stack direction='row' spacing={1} alignItems='flex-start'>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 400,
                    color: '#6B6760',
                    fontSize: '12px',
                    lineHeight: '18px',
                  }}
                >
                  <Translate expression='client.ClientDetail.Tasks.TabLabels.Description' />
                </Typography>
                <Box
                  sx={{
                    'fontSize': '12px',
                    'lineHeight': '18px',
                    '& p': {
                      fontSize: '12px',
                      lineHeight: '18px',
                      m: 0,
                    },
                    '& *': {
                      fontSize: '12px',
                    },
                  }}
                >
                  <MarkdownViewer value={description} />
                </Box>
              </Stack>
            }
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 400,
                  color: '#6B6760',
                  fontSize: '12px',
                }}
              >
                <Translate expression='client.ClientDetail.Tasks.TabLabels.DueDate' />
                {' '}
                {
                  new Date(dueDate).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }
              </Typography>
              {
                connections.length > 0 &&
                  <>
                    <Box
                      sx={{
                        height: 4,
                        width: 4,
                        borderRadius: '50%',
                        backgroundColor: 'text.secondary',
                      }}
                    />
                    <Stack
                      direction='row'
                      spacing={-.5}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      {
                        connections.map((connection, index) => (
                          <ConnectionBadge
                            key={connection.connectionRn}
                            connection={connection}
                            sx={{
                              width: '20px',
                              height: '20px',
                              position: 'relative',
                              zIndex: index + 1,
                              fontSize: '10px',
                              outline: '2px solid',
                              outlineColor: 'background.paper',
                            }}
                          />
                        ))
                      }
                    </Stack>
                  </>
              }
            </Stack>
          </Stack>
        </Stack>

        <HoverToolBox
          toolBar={toolbarVisible}
          anchorEl={anchorEl}
          onClickEdit={() => onClickEditTask(task)}
          onClickDelete={() => removeTask(task)}
          hideTool={{
            emoji: true,
            copy: true,
            edit: false,
            delete: false,
            reply: true,
          }}
        />
      </Box>
    </Box>
  );
};

TaskView.propTypes = {
  handleSetStatusToComplete: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func.isRequired,
};

export default React.memo(TaskView);
